import React, { useState, useEffect } from 'react';
import { Camera, Upload, Sparkles, AlertTriangle, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { adminApi } from '../../lib/api';

export default function OcrMenuPage() {
  const [image, setImage] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [parsedItems, setParsedItems] = useState([]);
  const [rawText, setRawText] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [categories, setCategories] = useState([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState('');

  useEffect(() => {
    adminApi.getCategories().then(r => setCategories(r.data || [])).catch(() => {});
  }, []);

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setParsedItems([]);
    setRawText('');
    setSaved(false);
    setImage(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleParse = async () => {
    if (!image) {
      toast.error('Veuillez sélectionner une image');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('image', image);
      const res = await adminApi.parseOcrMenu(formData);
      setParsedItems(res.data.items || []);
      setRawText(res.data.rawText || '');
      if (res.data.items?.length === 0) {
        toast.warning("Aucun élément reconnu. Essayez une photo plus nette.");
      } else {
        toast.success(`${res.data.items.length} élément(s) reconnu(s)`);
      }
    } catch (e) {
      const msg = e.response?.data?.detail || "Erreur OCR";
      setError(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (parsedItems.length === 0) return;
    if (!selectedCategoryId) {
      toast.error('Veuillez sélectionner une catégorie pour ranger les plats importés');
      return;
    }
    setSaving(true);
    try {
      await adminApi.saveOcrMenu({ items: parsedItems, categoryId: selectedCategoryId });
      setSaved(true);
      toast.success(`${parsedItems.length} élément(s) enregistré(s) dans le menu`);
    } catch (e) {
      const msg = e.response?.data?.detail || "Erreur d'enregistrement";
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <Sparkles className="w-5 h-5 text-garden-700" />
        <h2 className="font-heading text-xl font-bold text-garden-800">Reconnaissance OCR du menu</h2>
      </div>
      <p className="text-sm text-charcoal/60">
        Prenez en photo la carte du jour écrite à la main et le système en extraira automatiquement les plats et prix pour les intégrer au menu.
      </p>

      {/* Upload zone */}
      <div className="border-2 border-dashed border-garden-300 rounded-xl p-8 text-center hover:border-garden-500 transition-colors">
        <Camera className="w-12 h-12 text-garden-400 mx-auto mb-4" />
        <label className="cursor-pointer inline-flex items-center gap-2 bg-garden-800 text-white px-6 py-3 rounded-full hover:bg-garden-900 transition-colors">
          <Upload size={18} />
          Sélectionner une image
          <input type="file" accept="image/*" capture="environment" className="hidden" onChange={handleFileChange} />
        </label>
        <p className="text-xs text-charcoal/40 mt-3">JPG, PNG — max 10 Mo</p>
      </div>

      {/* Preview */}
      {previewUrl && (
        <div className="flex justify-center">
          <img src={previewUrl} alt="Photo du menu" className="max-h-64 rounded-xl shadow-md object-contain" />
        </div>
      )}

      {/* Parse button */}
      {image && (
        <div className="text-center">
          <button
            onClick={handleParse}
            disabled={loading}
            className="bg-garden-800 text-white px-8 py-3 rounded-full font-heading font-semibold hover:bg-garden-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors inline-flex items-center gap-2"
          >
            {loading ? (
              <>
                <Loader2 className="animate-spin" size={18} />
                Analyse en cours...
              </>
            ) : (
              <>
                <Sparkles size={18} />
                Analyser le menu
              </>
            )}
          </button>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-red-500 shrink-0 mt-0.5" />
          <p className="text-sm text-red-700">{error}</p>
        </div>
      )}

      {/* Parsed items */}
      {parsedItems.length > 0 && (
        <div className="space-y-4">
          <h3 className="font-heading font-semibold text-charcoal">
            Éléments reconnus ({parsedItems.length})
          </h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-garden-200">
                  <th className="text-left py-2 px-3 text-charcoal/60 font-medium">Plat</th>
                  <th className="text-right py-2 px-3 text-charcoal/60 font-medium">Prix</th>
                </tr>
              </thead>
              <tbody>
                {parsedItems.map((item, i) => (
                  <tr key={i} className="border-b border-garden-100 hover:bg-garden-50/50">
                    <td className="py-2 px-3">{item.name}</td>
                    <td className="py-2 px-3 text-right font-mono">{item.price.toFixed(2)} €</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-3 mb-2">
            <label className="text-sm font-medium text-charcoal shrink-0">Catégorie :</label>
            <select
              value={selectedCategoryId}
              onChange={e => setSelectedCategoryId(e.target.value)}
              className="flex-1 border border-garden-200 rounded-lg px-3 py-2 text-sm bg-white focus:ring-2 focus:ring-garden-500 focus:border-garden-500"
            >
              <option value="">Sélectionner une catégorie...</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
          <div className="flex gap-3">
            <button
              onClick={handleSave}
              disabled={saving}
              className="bg-garden-800 text-white px-6 py-2.5 rounded-full font-heading font-semibold hover:bg-garden-900 disabled:opacity-50 transition-colors inline-flex items-center gap-2"
            >
              {saving ? (
                <>
                  <Loader2 className="animate-spin" size={16} />
                  Enregistrement...
                </>
              ) : (
                <>
                  <CheckCircle2 size={16} />
                  Enregistrer dans le menu
                </>
              )}
            </button>
            {saved && (
              <span className="text-green-600 text-sm flex items-center gap-1">
                <CheckCircle2 size={16} /> Sauvegardé
              </span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}